import { useCallback, useEffect, useState } from "react";
import { useScoreApi, unwrap } from "../api/scoreApi";

// #2373 — superuser foxbox instance config. Instance-level, not show-scoped
// (App.jsx wires this page's route outside any active-show dependency), so
// this hook fetches once on mount and never re-fetches on active-show or
// active-org change. Consumes #2372's GET /instance (list every row) and
// PUT /instance/{instanceId}/config; both are super-only on the server, so a
// non-super caller here would only ever reach this hook via a hidden nav
// item plus a typed URL — see FoxboxInstances.jsx's own isSuper redirect,
// which is the actual AC5 gate.
export function useFoxboxInstances() {
  const scoreApi = useScoreApi();
  const [state, setState] = useState({ instances: [], loading: true, error: null });

  const refetch = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const instances = unwrap(await scoreApi.instance.listInstances());
      setState({ instances: Array.isArray(instances) ? instances : [], loading: false, error: null });
    } catch (err) {
      setState({ instances: [], loading: false, error: err });
    }
  }, [scoreApi]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Read-modify-write per #2372's route contract: enabled + the FULL shows[]
  // array travel together in one PUT, so the caller must pass the complete
  // draft (not a partial patch) or an untouched show's edited sibling would
  // clobber it. Returns the server's post-write document (the new source of
  // truth for that row) so the caller can reconcile local draft state.
  const saveInstance = useCallback(
    async (instanceId, { enabled, shows }) => {
      const updated = unwrap(
        await scoreApi.instance.putInstanceConfig(instanceId, { enabled, shows })
      );
      setState((s) => ({
        ...s,
        instances: s.instances.map((i) => (i.instanceId === instanceId ? updated : i)),
      }));
      return updated;
    },
    [scoreApi]
  );

  return { ...state, refetch, saveInstance };
}

export default useFoxboxInstances;
